import { Link } from "react-router-dom";

const steps = [
  {
    n: "01",
    icon: "ri-user-line",
    title: "Your handicap",
    body: "Your current WHS™ Handicap Index — the baseline number of strokes you receive.",
  },
  {
    n: "02",
    icon: "ri-flag-2-line",
    title: "The tees you play",
    body: "Course Rating and Slope of the tee you chose, used to set your Course Handicap.",
  },
  {
    n: "03",
    icon: "ri-pie-chart-2-line",
    title: "The format you play",
    body: "Stroke play, match play, fourball, foursomes — each has a different WHS™ allowance.",
  },
];

export default function HowItWorks() {
  return (
    <section className="bg-background-100 py-20 md:py-28 border-y border-background-200">
      <div className="mx-auto max-w-content px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <div>
          <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">How it works</span>
          <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl lg:text-5xl leading-tight">
            <a href="#how-it-works">What is a WHS™ shot allowance?</a>
          </h2>
          <p className="mt-5 text-foreground-700 text-base md:text-lg leading-relaxed">
            Under the World Handicap System™, the strokes you receive in a round depend on three things.
            Your Course Handicap converts your Handicap Index to the difficulty of the course. Your Playing
            Handicap then adjusts this again using the WHS™ allowance percentages for your chosen format.
          </p>
          <p className="mt-4 text-foreground-700 text-base leading-relaxed">
            HANDICALC® automates the entire process so you always use the correct numbers — no charts,
            no lookup tables, no mistakes.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-foreground-950 hover:bg-foreground-900 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              See how Handicalc works <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/formats"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-background-50 hover:bg-background-200 border border-background-300 text-foreground-900 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              View format allowances
            </Link>
          </div>
        </div>

        <div className="space-y-4">
          {steps.map((s) => (
            <div
              key={s.n}
              className="relative rounded-xl bg-background-50 border border-background-200 p-6 md:p-7 flex gap-5"
            >
              <div className="flex flex-col items-center">
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-600 text-background-50">
                  <i className={`${s.icon} text-lg`}></i>
                </span>
                <span className="mt-2 font-heading font-bold text-foreground-300 text-sm">{s.n}</span>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground-950 text-lg md:text-xl">{s.title}</h3>
                <p className="mt-2 text-sm md:text-base text-foreground-700 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}

          {/* Equation strip */}
          <div className="rounded-xl bg-primary-900 text-background-50 p-6 md:p-7">
            <div className="text-xs uppercase tracking-[0.18em] text-primary-200 font-semibold">The formula</div>
            <div className="mt-3 font-heading text-lg md:text-2xl leading-snug">
              Course Handicap × Format Allowance % = <span className="text-accent-300 italic">Playing Handicap</span>
            </div>
            <div className="mt-2 text-xs text-primary-100">
              e.g. CH 14 × 95% (singles stroke play) = PH 13
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}